import { useCallback, useMemo } from 'react'
import { useDispatch } from 'APP/Hooks/redux'
import moment, { Moment } from 'moment'

import AppointmentBookingRedux from 'APP/Redux/AppointmentBookingRedux'
import { providerBookingActions } from 'APP/Store/ProviderBooking'
import { AppointmentType } from '@dialogue/timekeeper'
import { FetchAvailabilitiesRequestPayload } from 'APP/Components/AppointmentBooking/utils/types'

/**
 * Composable for fetching a day's availabilities in the time based booking flow.
 */
export const useTimeBasedAvailabilities = (bookingId: string, date_from: string) => {
  const dispatch = useDispatch()

  const handleGetAvailabilities = useCallback(
    (day: Moment) => {
      const dayStart = day.clone().startOf('day')

      const effectiveDay =
        dayStart < moment(date_from).startOf('day') ? moment(date_from).startOf('day') : dayStart

      const dispatchPayload: FetchAvailabilitiesRequestPayload = {
        bookingId,
        day: effectiveDay.format(),
        params: { start_at: dayStart.format() },
      }

      dispatch(AppointmentBookingRedux.getAvailableAppointments(dispatchPayload))
    },
    [bookingId, date_from, dispatch]
  )

  return useMemo(() => ({ handleGetAvailabilities }), [handleGetAvailabilities])
}

/**
 * Composable for fetching a day's availabilities in the provider based booking flow.
 */
export const useProviderBasedAvailabilities = (params: {
  appointment_type: AppointmentType
  booking_id: string
  date_from: string
  date_until: string
}) => {
  const dispatch = useDispatch()

  const { appointment_type, booking_id, date_from, date_until } = params

  const handleGetAvailabilities = useCallback(
    (day: Moment) => {
      // A day narrows the search to that day, within the booking window.
      const start_at = moment.max(day.clone().startOf('day'), moment(date_from)).format()
      const end_at = moment.min(day.clone().endOf('day'), moment(date_until)).format()

      dispatch(
        providerBookingActions.getAvailabilities({
          bookingId: booking_id,
          appointment_type,
          start_at,
          end_at,
        })
      )
    },
    [appointment_type, booking_id, date_from, date_until, dispatch]
  )

  return useMemo(() => ({ handleGetAvailabilities }), [handleGetAvailabilities])
}
